
import { IAction, IThunkAction, IDispatch } from '../actions/createActionCreator';
import { IMiddleware } from './IMiddleware';
import { assign } from '../../utilities/objects';
import { AutoKeyFacet } from '../../models/AutoKeyFacet';
import { Facet, IFaceted } from '../../models/Facet';
import { IStore } from '../createStore';

const runOnceFacet = new Facet<string>('runOnce');

const runOnceKeyFacet = new AutoKeyFacet('runOnce');

/**
 * Wraps a to-be-dispatched thunk action with a version which can only be run once;
 * all subsequent dispatches of the thunk action result the result of the original dispatch.
 * @public
 */
export function asOnceOnly<R, S, E>(action: IThunkAction<R, S, E>): IThunkAction<R, S, E> {
    return assign((dispatch: IDispatch<S>) => {
        return dispatch(action);
    }, runOnceFacet.pack(runOnceKeyFacet.serializeNext()));
}

/**
 * @public
 */
export const runOnceMiddleware: IMiddleware = <S>({ dispatch }: Pick<IStore<S>, 'dispatch'>) => {
    const actionResults: {
        [key: string]: {};
    } = {};

    return (next: IDispatch<S>) => {
        return <R>(action: IAction | IThunkAction<R, S, {}>) => {
            if (typeof action === 'function') {
                const runOnce = runOnceFacet.unpack(<IFaceted><{}>action);

                if (runOnce) {
                    const previousActionResult = actionResults[runOnce];

                    if (runOnce in actionResults) {
                        return previousActionResult;
                    }

                    const result = next(action);

                    actionResults[runOnce] = result;

                    return result;
                }

                return next(action);
            } else {
                return next(action);
            }
        };
    };
};
